export const INTERVIEW_ID_KEY = "active_interview_id";
export const PROGRESS_KEY = (id) => `interview_progress_${id}`;
export const LOG_KEY = (id) => `interview_log_${id}`;
export const SESSION_KEY = (id) => `interview_session_${id}`;
export const FEEDBACK_KEY = (id) => `interview_feedback_${id}`;

// ── interviewId ─────────────────────────────────────────────────────────────
export const persistInterviewId = (id) => {
  try {
    if (id) localStorage.setItem(INTERVIEW_ID_KEY, id);
  } catch (_) {}
};

export const getPersistedId = () => {
  try {
    return localStorage.getItem(INTERVIEW_ID_KEY) || null;
  } catch (_) {
    return null;
  }
};

export const clearPersistedId = () => {
  try {
    localStorage.removeItem(INTERVIEW_ID_KEY);
  } catch (_) {}
};

// ── progress ────────────────────────────────────────────────────────────────
export const saveProgress = (id, data) => {
  try {
    localStorage.setItem(PROGRESS_KEY(id), JSON.stringify(data));
  } catch (_) {}
};

export const loadProgress = (id) => {
  try {
    const r = localStorage.getItem(PROGRESS_KEY(id));
    return r ? JSON.parse(r) : null;
  } catch (_) {
    return null;
  }
};

export const clearProgress = (id) => {
  try {
    localStorage.removeItem(PROGRESS_KEY(id));
  } catch (_) {}
};

// ── session tracker ─────────────────────────────────────────────────────────
export const loadSession = (id) => {
  try {
    const r = localStorage.getItem(SESSION_KEY(id));
    return r ? JSON.parse(r) : null;
  } catch (_) {
    return null;
  }
};

export const saveSession = (id, data) => {
  try {
    localStorage.setItem(SESSION_KEY(id), JSON.stringify(data));
  } catch (_) {}
};

export const clearSession = (id) => {
  try {
    localStorage.removeItem(SESSION_KEY(id));
  } catch (_) {}
};

// ── feedback progress (modal state) ──────────────────────────────────────────
export const saveFeedbackState = (id, data) => {
  try {
    if (!id) return;
    localStorage.setItem(FEEDBACK_KEY(id), JSON.stringify(data));
  } catch (_) {}
};

export const loadFeedbackState = (id) => {
  try {
    if (!id) return null;
    const r = localStorage.getItem(FEEDBACK_KEY(id));
    return r ? JSON.parse(r) : null;
  } catch (_) {
    return null;
  }
};

export const clearFeedbackState = (id) => {
  try {
    if (!id) return;
    localStorage.removeItem(FEEDBACK_KEY(id));
  } catch (_) {}
};

// ── time helpers ────────────────────────────────────────────────────────────
// FIX 1: was using {} braces without return — always returned undefined.
// Using arrow function with () so the expression is implicitly returned.
// FIX 2: subtracting timezone offset from UTC gives correct local wall-clock
// time, then slice(0,19) trims the trailing ".000Z" → "YYYY-MM-DDTHH:MM:SS"
const toLocalIsoSeconds = (d) => {
  const pad2 = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

// Stable stringify for de-dupe comparisons (order-independent).
const stableStringify = (value) => {
  try {
    if (value == null) return "null";
    if (typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
  } catch {
    return "";
  }
};

// ── logs ────────────────────────────────────────────────────────────────────
export const addLog = (id, event, question = null, extra = {}) => {
  try {
    if (!id) return;
    const logs = JSON.parse(localStorage.getItem(LOG_KEY(id)) || "[]");
    const now = new Date();
    const session = loadSession(id);
    const nowMs = now.getTime();
    const getEventName = (l) => l?.event_name ?? l?.event_Name ?? l?.eventName ?? l?.event ?? null;
    const getTsMs = (l) => {
      const t = l?.timestamp;
      if (!t) return null;
      const ms = new Date(t).getTime();
      return Number.isFinite(ms) ? ms : null;
    };
    const getQ = (l) => l?.questionNum ?? l?.question ?? null;

    // Normalize total question count across different callers.
    const totalQuestion =
      extra?.totalQuestion ??
      extra?.totalQuestions ??
      extra?.totalQ ??
      session?.totalQuestion ??
      session?.totalQuestions ??
      session?.totalQ ??
      null;

    // Keep payload consistent: store ONLY `totalQuestion` (avoid duplicates).
    const sanitizedExtra = { ...extra };
    delete sanitizedExtra.totalQuestion;
    delete sanitizedExtra.totalQuestions;
    delete sanitizedExtra.totalQ;

    // Normalize question text across inconsistent caller spellings/casing.
    const questionText =
      sanitizedExtra?.questionText ??
      sanitizedExtra?.QuestionText ??
      sanitizedExtra?.QusetionText ??
      null;
    delete sanitizedExtra.questionText;
    delete sanitizedExtra.QuestionText;
    delete sanitizedExtra.QusetionText;

    // Normalize counters to a single key: `count` (never `attemptCount`).
    let count =
      typeof sanitizedExtra?.count === "number"
        ? sanitizedExtra.count
        : typeof sanitizedExtra?.attemptCount === "number"
          ? sanitizedExtra.attemptCount
          : undefined;
    delete sanitizedExtra.count;
    delete sanitizedExtra.attemptCount;

    // Auto-count certain events when caller doesn't pass `count`.
    // This avoids having to send `{ count: n }` from every trigger.
    const AUTO_COUNT_EVENTS = new Set([
      "QUESTION_RECORDING_STARTED",
      "CAMERA_ACCESS_GRANTED",
      "MIC_ACCESS_GRANTED",
      "CAMERA_MIC_ACCESS_GRANTED",
      // Device/permission issues (should still carry count in payload)
      "CAMERA_MIC_ACCESS_DENIED",
      "CAMERA_REVOKED_MID_INTERVIEW",
      "MIC_REVOKED_MID_INTERVIEW",
      "CAMERA_MIC_REVOKED_MID_INTERVIEW",
    ]);
    if (typeof count !== "number" && AUTO_COUNT_EVENTS.has(event)) {
      const prior = logs.filter(
        (l) =>
          l?.event_name === event ||
          l?.event_Name === event ||
          l?.eventName === event ||
          l?.event === event,
      ).length;
      count = prior + 1;
    }

    // ── De-dupe identical events fired twice quickly ─────────────────────────
    // Some browser behaviors (reload/mount, permissions onchange) can trigger
    // the same log twice within a very short interval. Prevent noisy duplicates.
    const DEDUPE_WINDOW_MS = 1500;
    const last = logs[logs.length - 1];
    if (last) {
      const lastEvent = getEventName(last);
      const lastQ = getQ(last);
      const lastMs = getTsMs(last);
      const sameEvent = lastEvent === event;
      const sameQuestion = (lastQ ?? null) === (question ?? null);
      const closeInTime = lastMs != null && Math.abs(nowMs - lastMs) <= DEDUPE_WINDOW_MS;
      if (sameEvent && sameQuestion && closeInTime) {
        // Compare normalized payloads (excluding timestamps) to suppress only true duplicates.
        const comparableIncoming = stableStringify({
          event_name: event,
          questionNum: question ?? null,
          ...sanitizedExtra,
          ...(typeof count === "number" ? { count } : {}),
          ...(typeof totalQuestion === "number" ? { totalQuestion } : {}),
          ...(typeof questionText === "string" && questionText.trim() ? { questionText } : {}),
        });
        const { timestamp, localTime, interviewId, event, ...restLast } = last || {};
        const comparableLast = stableStringify({
          event_name: lastEvent,
          questionNum: lastQ ?? null,
          ...restLast,
        });

        // If payload is identical, suppress; if something differs (e.g., count increment),
        // allow the new log through.
        if (comparableIncoming && comparableIncoming === comparableLast) {
          return;
        }
      }
    }

    // ── Collapse noisy camera/mic logs into a single meaningful event ─────────
    // If camera+mic events happen together, keep only a common log.
    // This avoids three logs (camera, mic, both) for the same action.
    const MERGE_WINDOW_MS = 2000;
    const isWithinWindow = (l) => {
      const ms = getTsMs(l);
      return ms != null && Math.abs(nowMs - ms) <= MERGE_WINDOW_MS;
    };

    // Grant collapse: CAMERA_ACCESS_GRANTED + MIC_ACCESS_GRANTED → CAMERA_MIC_ACCESS_GRANTED
    if (event === "CAMERA_ACCESS_GRANTED" || event === "MIC_ACCESS_GRANTED") {
      const other = event === "CAMERA_ACCESS_GRANTED" ? "MIC_ACCESS_GRANTED" : "CAMERA_ACCESS_GRANTED";
      const last = logs[logs.length - 1];
      if (last && isWithinWindow(last) && getEventName(last) === other) {
        // Replace last entry with common log and skip adding this one.
        last.event_name = "CAMERA_MIC_ACCESS_GRANTED";
        last.device = "camera+microphone";
        // keep latest time for the merged event
        last.timestamp = now.toISOString();
        last.localTime = now.toISOString();
        localStorage.setItem(LOG_KEY(id), JSON.stringify(logs));
        return;
      }
    }

    // If common grant comes after individual logs, drop individuals within window.
    if (event === "CAMERA_MIC_ACCESS_GRANTED") {
      while (logs.length) {
        const last = logs[logs.length - 1];
        const en = getEventName(last);
        if (!isWithinWindow(last)) break;
        if (en === "CAMERA_ACCESS_GRANTED" || en === "MIC_ACCESS_GRANTED") {
          logs.pop();
          continue;
        }
        break;
      }
    }

    // Revoke collapse: CAMERA_REVOKED_MID_INTERVIEW + MIC_REVOKED_MID_INTERVIEW → CAMERA_MIC_REVOKED_MID_INTERVIEW
    if (event === "CAMERA_REVOKED_MID_INTERVIEW" || event === "MIC_REVOKED_MID_INTERVIEW") {
      const other = event === "CAMERA_REVOKED_MID_INTERVIEW" ? "MIC_REVOKED_MID_INTERVIEW" : "CAMERA_REVOKED_MID_INTERVIEW";
      const last = logs[logs.length - 1];
      if (last && isWithinWindow(last) && getEventName(last) === other) {
        const lastCount = typeof last.count === "number" ? last.count : 1;
        const incomingCount = typeof count === "number" ? count : 1;
        last.event_name = "CAMERA_MIC_REVOKED_MID_INTERVIEW";
        last.device = "camera+microphone";
        last.count = Math.max(lastCount, incomingCount);
        last.timestamp = now.toISOString();
        last.localTime = now.toISOString();
        localStorage.setItem(LOG_KEY(id), JSON.stringify(logs));
        return;
      }
    }

    const entry = {
      event_name: event,
      // Backward compatibility: some code filters on `event` (legacy key).
      event,
      interviewId: id,
      questionNum: question,
      ...sanitizedExtra,
      timestamp: now.toISOString(),      // e.g. "2026-03-17T10:33:59.509Z"
      // Local wall-clock time (no timezone suffix), "YYYY-MM-DDTHH:MM:SS"
      localTime: now.toISOString(),
    };

    // Only attach related fields when they exist (never store null/blank keys).
    if (typeof count === "number") entry.count = count;
    if (typeof totalQuestion === "number") entry.totalQuestion = totalQuestion;
    if (typeof questionText === "string" && questionText.trim()) entry.questionText = questionText;
    logs.push(entry);
    localStorage.setItem(LOG_KEY(id), JSON.stringify(logs));
    //console.log("[Interview Log]", entry);
  } catch (_) {
    // Never throw — logging must never break functionality
  }
};

export const getLogs = (id) => {
  try {
    return JSON.parse(localStorage.getItem(LOG_KEY(id)) || "[]");
  } catch (_) {
    return [];
  }
};

export const clearLogs = (id) => {
  try {
    localStorage.removeItem(LOG_KEY(id));
  } catch (_) {}
};

// Clear ALL localStorage keys for one interviewId (full reset)
export const clearAllForInterview = (id) => {
  clearProgress(id);
  clearLogs(id);
  clearSession(id);
};